// 261023 E3-P3j2b-2 (D8) — «Để máy sửa N tập»: thứ tự nút chính, chip và các câu của máy tự cứu. THUẦN (jest), KHÔNG import antd/react. Không số tiền.
// Nguồn: `workbench.autoRepair` (BE; cờ `ADAPTATION_AUTO_REPAIR` tắt ⇒ khoá vắng ⇒ mọi hàm trả null/rỗng ⇒ giao diện y như trước).
import type { EpisodeScriptRecord } from '../../../types/adaptation';
import type { WorkbenchAutoRepair } from '../../../types/adaptationWorkbench';
import { friendlyApiError } from './jobErrorText';

export const autoRepairLabel = (n: number): string => `Để máy sửa ${n} tập`;
export const AUTO_REPAIR_ONE_LABEL = 'Để máy sửa tập này';
export const AUTO_REPAIR_STOP_LABEL = 'Dừng';

export interface AutoRepairStrip {
  mode: 'running' | 'ready';
  /** Tập máy sẽ sửa (`ready`) — rỗng khi `running`. */
  episodes: number[];
  /** MỘT câu cho dải. */
  sentence: string;
  /** Nhãn nút đỏ (`ready`) — '' khi `running`. */
  label: string;
}

type Auto = WorkbenchAutoRepair | null | undefined;

export const runningSentence = (episode: number | null | undefined): string => (typeof episode === 'number' ? `Máy đang sửa tập ${episode}…` : 'Máy đang sửa…');

/** Dải/nút chính: đang chạy ⇒ `running` (KHÔNG nút đỏ; có «Dừng»); có tập đủ điều kiện và bấm được ⇒ `ready`; còn lại null (kể cả `available=false` vì thiếu giá/cấu hình — không vẽ nút giả). */
export const autoRepairStripOf = (auto: Auto): AutoRepairStrip | null => {
  if (!auto) return null;
  if (auto.running) return { mode: 'running', episodes: [], sentence: runningSentence(auto.running.episode), label: '' };
  const eps = [...(auto.eligibleEpisodes ?? [])].sort((a, b) => a - b);
  if (!auto.available || eps.length === 0) return null;
  const sentence = eps.length === 1 ? `Tập ${eps[0]} chưa đạt kiểm tra — máy có thể tự sửa trong giới hạn`
    : `${eps.length} tập chưa đạt kiểm tra${eps.length <= 6 ? ` (tập ${eps.join(', ')})` : ''} — máy có thể tự sửa trong giới hạn`;
  return { mode: 'ready', episodes: eps, sentence, label: autoRepairLabel(eps.length) };
};

/** Nút «Để máy sửa tập này» ở trang tập: chỉ khi tập nằm trong `eligibleEpisodes`, bấm được và chưa có lần sửa nào đang chạy. */
export const autoRepairOneOf = (auto: Auto, ep: number): boolean =>
  !!auto && !auto.running && auto.available && (auto.eligibleEpisodes ?? []).includes(ep);

// ─── Câu cho dòng tập (D8 — 3 câu LỆCH mockup, báo User) ─────────────────────

export const gaveUpSentence = (ep: number): string => `Máy đã thử hết cách trong giới hạn, tập ${ep} vẫn chưa ổn.`;
export const EDITED_SENTENCE = 'Máy giữ nguyên vì bạn đã chỉnh tập này.';
export const PUSHED_SENTENCE = 'Máy giữ nguyên vì tập đã đẩy.';
export const LIMIT_SENTENCE = 'Máy dừng vì đã chạm giới hạn của lần sửa này.';
export const SKIPPED_SENTENCE = 'Máy chưa sửa được tập này vì thiếu thông tin cần để kiểm.';
export const AUTO_SHORT = { gaveUp: 'Máy hết cách trong giới hạn', edited: 'Máy giữ nguyên: bạn đã chỉnh', pushed: 'Máy giữ nguyên: đã đẩy', limit: 'Máy dừng: chạm giới hạn', skipped: 'Máy thiếu thông tin để kiểm' } as const;

export type AutoNoteKind = 'gaveUp' | 'edited' | 'pushed' | 'limit' | 'skipped';
export interface AutoNote { kind: AutoNoteKind; sentence: string; short: string }

/** Việc máy đã làm/không làm với MỘT tập. Ưu tiên: bị giữ nguyên (đã chỉnh / đã đẩy) › dừng ở lần bấm trước › hết cách. null = không có gì để nói. */
const note = (kind: AutoNoteKind): AutoNote => ({ kind, sentence: '', short: AUTO_SHORT[kind] });
const stoppedKind = (reason: string): AutoNoteKind | null => {
  if (reason.startsWith('LIMIT_')) return 'limit';
  if (reason.startsWith('PROTECTED_') || reason === 'BASIS_CHANGED' || reason === 'SCRIPT_CHANGED') return 'edited';
  if (reason === 'PUSHED' || reason === 'BEFORE_PUSHED') return 'pushed';
  return null;
};
export const autoRepairNoteOf = (auto: Auto, ep: number): AutoNote | null => {
  if (!auto) return null;
  const sentenceOf = (k: AutoNoteKind): string => (k === 'edited' ? EDITED_SENTENCE : k === 'pushed' ? PUSHED_SENTENCE : k === 'limit' ? LIMIT_SENTENCE : k === 'skipped' ? SKIPPED_SENTENCE : gaveUpSentence(ep));
  const make = (k: AutoNoteKind): AutoNote => ({ ...note(k), sentence: sentenceOf(k) });
  const kept = (auto.kept ?? []).find((k) => k.episode === ep);
  if (kept) return make(kept.reason === 'PUSHED' ? 'pushed' : 'edited');
  const stopped = (auto.last?.stopped ?? []).find((s) => s.episode === ep);
  const sk = stopped ? stoppedKind(stopped.reason) : null;
  if (sk) return make(sk);
  if ((auto.gaveUp ?? []).includes(ep)) return make('gaveUp');
  if ((auto.skipped ?? []).some((x) => x.episode === ep)) return make('skipped');   // R5: mọi mã (kể cả lạ) ⇒ cùng MỘT câu, không lộ mã
  return null;
};

/** Tập BE báo đang được sửa (chip «Đang sửa»). */
export const autoRepairFixingOf = (auto: Auto): number[] => (typeof auto?.running?.episode === 'number' ? [auto.running.episode] : []);

/** «Máy đã tự sửa N chỗ»: số lỗi máy đã gỡ khỏi biên lai `qualityReview.autoRepair` (0 khi không có / người đã Hoàn tác). */
export const autoRepairResolvedCount = (script: Pick<EpisodeScriptRecord, 'script'> | null | undefined): number => {
  const qr = script?.script?.qualityReview as { version?: number; autoRepair?: { resolvedFindingKeys?: unknown } } | undefined;
  const keys = qr?.version === 2 ? qr.autoRepair?.resolvedFindingKeys : undefined;
  return Array.isArray(keys) ? new Set(keys.filter((k): k is string => typeof k === 'string' && k !== '')).size : 0;
};

/**
 * Dải khi job `auto_repair` đang chạy: «Máy đang sửa tập n…» thay cho «Đang viết kịch bản ở nền — … · job <mã>…». null = không phải job của máy tự sửa (giữ dòng cũ).
 * Nguồn: `autoRepair.running` (có tập) hoặc job phase `auto_repair` trong `jobs.running` (workbench chưa kịp báo tập).
 */
export const autoRepairJobLineOf = (w: { autoRepair?: WorkbenchAutoRepair | null; jobs?: { running?: ReadonlyArray<{ phase?: string }> } | null } | null | undefined): string | null => {
  if (w?.autoRepair?.running) return runningSentence(w.autoRepair.running.episode);
  return (w?.jobs?.running ?? []).some((j) => j.phase === 'auto_repair') ? runningSentence(null) : null;
};

// ─── requestId (bấm hai lần không mở hai job) ───────────────────────────────

export interface RequestSlot { id: string | null; key: string }
export const newRequestSlot = (): RequestSlot => ({ id: null, key: '' });
/** Cùng việc (cùng `key`) chưa có kết quả chắc chắn ⇒ DÙNG LẠI đúng `requestId` (BE trả job cũ, không mở thêm). Việc khác ⇒ id mới. */
export const takeRequestId = (slot: RequestSlot, key: string, make: () => string): string => {
  if (slot.id !== null && slot.key === key) return slot.id;
  slot.id = make(); slot.key = key;
  return slot.id;
};
/** Có kết quả chắc chắn (thành công, hoặc BE từ chối bằng mã 4xx): lần bấm sau là việc mới. Lỗi mạng / 5xx ⇒ KHÔNG gọi (giữ id để bấm lại idempotent). */
export const settleRequest = (slot: RequestSlot): void => { slot.id = null; slot.key = ''; };

export interface AutoRepairErrorView { text: string; /** Nên tải lại workbench (trạng thái đã đổi). */ reload: boolean; /** Lỗi chắc chắn (BE từ chối) ⇒ giải phóng `requestId`. */ settled: boolean }
const KNOWN: Record<string, { text: string; reload: boolean }> = {
  NO_ELIGIBLE_EPISODES: { text: 'Không còn tập nào máy sửa được — đã tải lại', reload: true },
  EPISODES_NOT_ELIGIBLE: { text: 'Tập này máy không sửa được nữa — đã tải lại', reload: true },
  JOB_RUNNING: { text: 'Đang có việc chạy nền — đợi xong rồi bấm', reload: true },
  APPEND_RUN_ACTIVE: { text: 'Đang chạy chuỗi truyện mới — đợi xong rồi bấm', reload: true },
  AUTO_REPAIR_DISABLED: { text: 'Tính năng máy tự sửa chưa bật', reload: true },
  AUTO_REPAIR_NOT_RUNNING: { text: 'Máy không còn đang sửa — đã tải lại', reload: true },
  ESTIMATE_UNKNOWN: { text: 'Chưa đủ thông tin để máy tự sửa — thử lại sau', reload: true },
  OUTLINE_NOT_APPROVED: { text: 'Khung xương chưa duyệt — duyệt trước rồi bấm', reload: true },
};
export const autoRepairErrorView = (e: unknown): AutoRepairErrorView => {
  const r = (e as { response?: { status?: number; data?: { code?: unknown } } } | null | undefined)?.response;
  const code = typeof r?.data?.code === 'string' ? r.data.code : '';
  const known = KNOWN[code];
  if (known) return { ...known, settled: true };
  const status = r?.status;
  const settled = typeof status === 'number' && status >= 400 && status < 500;   // BE từ chối ⇒ chưa mở job ⇒ lần sau là việc mới; lỗi mạng / 5xx ⇒ giữ id
  return { text: friendlyApiError(e, 'Không bắt đầu được việc máy tự sửa'), reload: settled, settled };
};
